<template>
  <div
    class="bg-white dark:bg-blueDianne-800 dark:text-white mt-10 rounded-xl py-8 flex flex-wrap"
  >
    <div class="w-full sm:w-1/3 md:w-1/4">
      <profile-image
        v-if="profile"
        class="mx-auto"
        :size="150"
        :img-url="imageUrlFromProfile(profile)"
      />
      <h3 class="text-center text-2xl px-4 mt-3 font-headline">
        {{ profile.fullDisplayName || profile.displayName }}
      </h3>
      <div
        v-if="location"
        class="text-center px-4 text-black/50 text-xs mt-3 font-headline italic"
      >
        <i class="fas fa-fw fa-map-marker-alt mr-2"></i>
        <span>{{ location }}</span>
      </div>
      <div
        v-if="profile.yearsOfExperience"
        class="text-center px-4 text-black/50 text-xs mt-2 font-headline"
      >
        <i class="fas fa-fw fa-history mr-2"></i>
        <span>{{ profile.yearsOfExperience }} Years Experience</span>
      </div>
      <div
        v-if="profile.publicationCount"
        class="text-center px-4 text-black/50 text-xs mt-2 font-headline"
      >
        <i class="fas fa-fw fa-book mr-2"></i>
        <span>{{ profile.publicationCount }} Publications</span>
      </div>
    </div>

    <div class="w-full sm:w-2/3 md:w-3/4 sm:border-l border-blueGray-200">
      <h5 class="text-lg px-4 md:px-8 mb-5 font-headline">
        {{ profile.headline }}
      </h5>
      <div
        v-if="
          (institutions?.length && relevantEducations.length) ||
          (!institutions?.length && profile.educations?.length)
        "
        class="w-full mb-10 px-4 md:px-8 mb-5"
      >
        <h6 class="uppercase font-light text-sm mb-2">Education</h6>
        <div class="ml-3">
          <div
            v-for="(education, index) in institutions?.length
              ? profile.educations
              : profile.educations.slice(0, 3)"
            :key="index"
            class="py-1 my-1"
          >
            <div
              v-if="
                !institutions?.length ||
                institutions.indexOf(education.gridInstituteId) > -1
              "
            >
              <h3 class="font-medium">
                <span
                  :class="{
                    'highlighted-swipe yellow-swipe px-1 italic':
                      institutions &&
                      institutions.indexOf(education.gridInstituteId) > -1,
                  }"
                >
                  {{ education.schoolName }}
                </span>
              </h3>
              <div class="font-light ml-3">
                <div>
                  <p
                    v-if="education.degree || education.specialization"
                    class="font-light"
                  >
                    <span v-if="education.degree">{{ education.degree }}</span>
                    <span v-if="education.degree && education.specialization"
                      >,
                    </span>
                    <span v-if="education.specialization">{{
                      education.specialization
                    }}</span>
                    <span
                      v-if="
                        (education.degree || education.specialization) &&
                        (education.graduationMonth || education.graduationYear)
                      "
                    >
                      /
                    </span>
                    <span v-if="education.graduationMonth">{{
                      education.graduationMonth[0]
                    }}</span>
                    <span
                      v-if="
                        education.graduationMonth && education.graduationYear
                      "
                      >,
                    </span>
                    <span v-if="education.graduationYear">{{
                      education.graduationYear
                    }}</span>
                    <span v-if="education.isComplete === false">
                      (anticipated)</span
                    >
                  </p>
                  <div
                    v-if="
                      education.schoolCity ||
                      education.schoolState ||
                      education.schoolCountry
                    "
                    class="flex items-center text-sm leading-normal mt-0 mb-2"
                  >
                    <i
                      class="fas fa-fw fa-map-marker-alt mr-1 text-black/50"
                    ></i>
                    <div>
                      <span
                        v-if="
                          education.schoolCity &&
                          education.schoolState &&
                          education.schoolCountry
                        "
                      >
                        {{ education.schoolCity }},
                        {{ education.schoolState[0] }},
                        {{ education.schoolCountry[0] }}
                      </span>
                      <span v-else-if="profile.city && education.schoolState">
                        {{ education.schoolCity }},
                        {{ education.schoolState[0] }}
                      </span>
                      <span v-else-if="education.schoolState">
                        {{ education.schoolState[0] }}
                      </span>
                      <span v-else-if="education.schoolCity">
                        {{ education.schoolCity }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="
          (institutions?.length && relevantExperiences.length) ||
          (!institutions?.length && profile.experiences?.length)
        "
        class="w-full mb-10 px-4 md:px-8 mb-5"
      >
        <h6 class="uppercase font-light text-sm mb-2">Experience</h6>
        <div class="ml-3">
          <div
            v-for="(experience, index) in institutions?.length
              ? profile.experiences
              : profile.experiences.slice(0, 3)"
            :key="index"
            class="py-1 mx-1 my-1"
          >
            <div
              v-if="
                !institutions?.length ||
                institutions.indexOf(experience.gridInstituteId) > -1
              "
            >
              <h3 class="font-medium">
                <span
                  :class="{
                    'bg-australianMint px-1 italic':
                      institutions &&
                      institutions.indexOf(experience.gridInstituteId) > -1,
                  }"
                >
                  {{ experience.companyName }}
                </span>
              </h3>
              <div class="font-light ml-3">
                <div>
                  <div
                    v-for="position in experience.positions"
                    :key="position.hashedId"
                  >
                    <p
                      v-if="
                        position.positionName ||
                        position.startMonth ||
                        position.startYear ||
                        position.endMonth ||
                        position.endYear
                      "
                    >
                      <span v-if="position.positionName">{{
                        position.positionName
                      }}</span>
                      <span
                        v-if="
                          position.positionName &&
                          (position.startMonth ||
                            position.startYear ||
                            position.endMonth ||
                            position.endYear)
                        "
                      >
                        /
                      </span>
                      <span
                        v-if="
                          position.startMonth ||
                          position.startYear ||
                          position.endMonth ||
                          position.endYear
                        "
                      >
                        <span v-if="position.startMonth">{{
                          position.startMonth[0]
                        }}</span>
                        <span v-if="position.startMonth && position.startYear"
                          >,
                        </span>
                        <span v-if="position.startYear">{{
                          position.startYear
                        }}</span>
                        &mdash;
                        <span v-if="position.endMonth || position.endYear">
                          <span v-if="position.endMonth">{{
                            position.endMonth[0]
                          }}</span>
                          <span v-if="position.endMonth && position.endYear"
                            >,
                          </span>
                          <span v-if="position.endYear">{{
                            position.endYear
                          }}</span>
                        </span>
                        <span v-else>
                          <span>Present</span>
                        </span>
                      </span>
                    </p>
                    <p v-if="position.description" class="text-sm mb-3">
                      {{ position.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="
          researchInterests?.length == 0 &&
          profile.researchInterests?.length > 0
        "
        class="w-full mb-10 px-4 md:px-8 mb-5"
      >
        <h6 class="uppercase font-light text-sm mb-2">Research Expertise</h6>
        <div class="ml-3">
          <div
            v-for="(researchInterest, index) in profile.researchInterests.slice(
              0,
              5,
            )"
            :key="index"
            class="inline-block mx-4 my-1 highlighted-swipe yellow-swipe text-md"
          >
            {{ researchInterest.researchInterest }}
          </div>
          <div
            v-if="profile.researchInterests.length - 5 > 0"
            class="inline-block text-sm px-2 py-1 mx-1 my-1"
          >
            And {{ profile.researchInterests.length - 5 }} more
          </div>
        </div>
      </div>
      <div
        v-if="researchInterests?.length && relevantResearchInterests.length"
        class="w-full mb-10 px-4 md:px-8 mb-5"
      >
        <h6 class="uppercase font-light text-sm mb-2">
          Most Relevant Research Expertise
        </h6>
        <div class="ml-3">
          <div
            v-for="(researchInterest, index) in relevantResearchInterests"
            :key="index"
            class="inline-block mx-4 my-1 highlighted-swipe yellow-swipe text-md"
          >
            {{ researchInterest.researchInterest }}
          </div>
        </div>
      </div>

      <div
        v-if="researchInterests?.length && lessRelevantResearchInterests"
        class="w-full mb-10 px-4 md:px-8 mb-5"
      >
        <h6
          v-if="relevantResearchInterests.length"
          class="uppercase font-light text-sm mb-2"
        >
          Other Research Expertise ({{ lessRelevantResearchInterests.length }})
        </h6>
        <h6 v-else class="uppercase font-light text-sm mb-2">
          Research Expertise ({{ lessRelevantResearchInterests.length }})
        </h6>
        <div class="ml-3">
          <div
            v-for="(researchInterest, index) in [
              ...lessRelevantResearchInterests,
            ].slice(0, 5)"
            :key="index"
            class="inline-block mx-4 my-1 highlighted-swipe yellow-swipe text-md"
          >
            {{ researchInterest.researchInterest }}
          </div>
          <div
            v-if="lessRelevantResearchInterests.length - 5 > 0"
            class="inline-block text-sm px-2 py-1 mx-1 my-1"
          >
            And {{ lessRelevantResearchInterests.length - 5 }} more
          </div>
        </div>
      </div>

      <div v-if="profile.about" class="w-full mb-10 px-4 md:px-8 mb-5">
        <h6 class="uppercase font-light text-sm mb-2">About</h6>
        <div class="ml-3">
          <client-only>
            <markdown-viewer v-if="profile?.about" :text="profile.about" />
          </client-only>
          <div v-if="isSsr">
            {{ profile.about }}
          </div>
        </div>
      </div>
      <div
        v-if="relevantPublications.length > 0"
        class="w-full px-4 md:px-8 mb-5"
      >
        <h6 class="uppercase font-light text-sm mb-2">
          Most Relevant Publications ({{ relevantPublications.length }}+)
        </h6>
        <p class="text-sm font-light text-black/50">
          {{ profile.publications.length }} total publications
        </p>
        <div class="ml-3">
          <div
            v-for="publication in relevantPublications"
            :key="publication.hashedId"
            class="my-5 text-sm"
          >
            <a
              :href="publication.url"
              target="_blank"
              @click="$event.stopPropagation()"
              ><h3 class="underline" v-html="publication.title"></h3
            ></a>
            <div class="font-light font-light text-sm">
              <div class="pb-3 pt-1">
                <p>
                  <span v-if="publication.containerTitle" class="font-medium">{{
                    publication.containerTitle
                  }}</span>
                  <span
                    v-if="publication.containerTitle && publication.publishedAt"
                  >
                    /
                  </span>
                  <span v-if="publication.publishedAt">{{
                    $dayjs(publication.publishedAt).format("MMM DD, YYYY")
                  }}</span>
                </p>
                <p class="text-xs">{{ publication.adjustedCitation }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full flex justify-end px-10">
      <p
        class="rounded-b-full rounded-l-full bg-blueDianne px-12 py-3 font-headline text-white font-bold"
      >
        See Full Profile
      </p>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { utils } from "common-frontend";
import ProfileImage from "./ProfileImage.vue";

const { imageUrlFromProfile } = utils;
const components = { ProfileImage };

if (!import.meta.env.SSR) {
  components.MarkdownViewer = defineAsyncComponent(
    () => import("./MarkdownViewer.vue"),
  );
}

export default {
  components,
  props: {
    profile: Object,
    researchInterests: Array,
    institutions: Array,
  },
  data() {
    return {
      imageUrlFromProfile,
      isSsr: import.meta.env.SSR,
    };
  },
  computed: {
    location() {
      if (this.profile.city && this.profile.state && this.profile.country) {
        return `${this.profile.city}, ${this.profile.state[0]}, ${this.profile.country[0]}`;
      }
      if (this.profile.city && this.profile.state) {
        return `${this.profile.city}, ${this.profile.state[0]}`;
      }
      if (this.profile.state) {
        return this.profile.state[0];
      }
      if (this.profile.city) {
        return this.profile.city;
      }
      return null;
    },
    relevantPublications() {
      if (this.researchInterests && this.profile.publications) {
        const researchInterestSlugs = this.researchInterests.map((item) => {
          return item.toLowerCase().replaceAll(" ", "-");
        });
        return this.profile.publications.filter((publication) => {
          return publication.subjects.some((r) => {
            return (
              researchInterestSlugs.indexOf(
                r.subject.toLowerCase().replaceAll(" ", "-"),
              ) >= 0
            );
          });
        });
      }
      return [];
    },
    relevantResearchInterests() {
      if (this.researchInterests && this.profile.researchInterests) {
        const researchInterestSlugs = this.researchInterests.map((item) => {
          return item
            .toLowerCase()
            .replaceAll(" ", "-")
            .replaceAll("-and-", "-")
            .replaceAll("-or-", "-");
        });
        return this.profile.researchInterests.filter((researchInterest) => {
          return (
            researchInterestSlugs.indexOf(
              researchInterest.researchInterest
                .toLowerCase()
                .replaceAll(" ", "-")
                .replaceAll("-and-", "-")
                .replaceAll("-or-", "-"),
            ) > -1
          );
        });
      }
      return [];
    },
    lessRelevantResearchInterests() {
      if (this.researchInterests && this.profile.researchInterests) {
        const researchInterestSlugs = this.researchInterests.map((item) => {
          return item.toLowerCase().replaceAll(" ", "-");
        });
        return this.profile.researchInterests.filter((researchInterest) => {
          return (
            researchInterestSlugs.indexOf(
              researchInterest.researchInterest
                .toLowerCase()
                .replaceAll(" ", "-"),
            ) === -1
          );
        });
      }
      return [];
    },
    relevantEducations() {
      if (this.institutions && this.profile.educations) {
        return this.profile.educations.filter((education) => {
          return this.institutions.indexOf(education.gridInstituteId) > -1;
        });
      }
      return [];
    },
    relevantExperiences() {
      if (this.institutions && this.profile.experiences) {
        return this.profile.experiences.filter((experience) => {
          return this.institutions.indexOf(experience.gridInstituteId) > -1;
        });
      }
      return [];
    },
  },
};
</script>
